import { withRouter } from "../utils/withRouter";
import api from "../api/index";
import React, { useCallback, useContext, useEffect, useMemo, useState, useRef } from "react";
import { cachedPost, readFreshCache } from "./Cache";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar
} from "recharts";
import { UserContext } from "../UserContext";
import LoadingComponent from "./LoadingComponent";

const ALL_MONTHS_OPTION = { label: "All Months", value: "all" };

const toDate = (value) => {
  if (!value) return null;
  const isoMonth = /^\d{4}-\d{2}$/.test(value) ? `${value}-01` : value;
  const date = new Date(isoMonth);
  return Number.isNaN(date.getTime()) ? null : date;
};

const sortChronologically = (data, accessor) => {
  if (!Array.isArray(data)) return [];
  return [...data].sort((a, b) => {
    const aDate = toDate(a?.[accessor]);
    const bDate = toDate(b?.[accessor]);
    if (aDate && bDate) return aDate - bDate;
    if (aDate) return -1;
    if (bDate) return 1;
    return 0;
  });
};

const parseYearMonth = (value) => {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})/.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (Number.isNaN(year) || Number.isNaN(month)) return null;
  return { year, month };
};

const getMonthKey = (dateStr) => {
  const parsed = parseYearMonth(dateStr);
  if (!parsed) return null;
  const { year, month } = parsed;
  return `${year}-${String(month).padStart(2, "0")}`;
};

const formatMonthLabel = (value) => {
  const parsed = parseYearMonth(value);
  if (!parsed) return value ?? "Unknown";
  const { year, month } = parsed;
  const date = new Date(year, month - 1, 1);
  return new Intl.DateTimeFormat(undefined, { month: "long", year: "numeric" }).format(date);
};

const buildMonthEntries = (data) => {
  if (!Array.isArray(data) || data.length === 0) return [];
  const labels = new Map();
  data.forEach((item) => {
    const key = getMonthKey(item?.date);
    if (!key || labels.has(key)) return;
    labels.set(key, formatMonthLabel(item.date));
  });
  return Array.from(labels.entries())
    .sort((a, b) => {
      const aDate = toDate(a[0]);
      const bDate = toDate(b[0]);
      if (aDate && bDate) return bDate - aDate;
      if (aDate) return -1;
      if (bDate) return 1;
      return a[0].localeCompare(b[0]);
    })
    .map(([value, label]) => ({ value, label }));
};

const normalizeAvailableMonths = (list) => {
  if (!Array.isArray(list)) return [];
  return list
    .map((entry) => {
      if (!entry) return null;
      if (typeof entry === "string") {
        const parts = entry.split("-");
        if (parts.length < 2) return null;
        const [year, month] = parts;
        const normalizedMonth = String(month).padStart(2, "0");
        const value = `${year}-${normalizedMonth}`;
        return { value, label: formatMonthLabel(`${value}-01`) };
      }
      const year = entry.year ?? entry.Year ?? entry.year_number ?? entry.yearValue;
      const monthRaw = entry.month ?? entry.Month ?? entry.month_number ?? entry.monthValue;
      if (year == null || monthRaw == null) return null;
      const monthNumber = Number(monthRaw);
      if (Number.isNaN(monthNumber)) return null;
      const normalizedMonth = String(monthNumber).padStart(2, "0");
      const value = `${year}-${normalizedMonth}`;
      return { value, label: formatMonthLabel(`${value}-01`) };
    })
    .filter(Boolean);
};

const combineMonthOptions = (availableList, dataEntries = []) => {
  const merged = new Map();
  [...(availableList || []), ...dataEntries].forEach((option) => {
    if (!option?.value || merged.has(option.value)) return;
    merged.set(option.value, option.label);
  });
  const sorted = Array.from(merged.entries()).sort((a, b) => {
    const aDate = toDate(a[0]);
    const bDate = toDate(b[0]);
    if (aDate && bDate) return bDate - aDate;
    if (aDate) return -1;
    if (bDate) return 1;
    return a[0].localeCompare(b[0]);
  });
  return [ALL_MONTHS_OPTION, ...sorted.map(([value, label]) => ({ value, label }))];
};

const mergeMonthOptions = (existingOptions = [ALL_MONTHS_OPTION], availableOptions = [], points = []) => {
  const normalizedExisting =
    Array.isArray(existingOptions) && existingOptions.length ? existingOptions : [ALL_MONTHS_OPTION];
  const existingEntries = normalizedExisting.slice(1);
  return combineMonthOptions(availableOptions, [...buildMonthEntries(points), ...existingEntries]);
};

const normalizeDailyAmounts = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.daily_transaction_amounts)) return data.daily_transaction_amounts;
  if (Array.isArray(data?.daily_amounts)) return data.daily_amounts;
  if (Array.isArray(data?.days)) {
    return data.days.map((day) => ({
      date: day.date,
      total_amount: typeof day.total_amount === "number" ? day.total_amount : Number(day.total_amount ?? 0),
    }));
  }
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const normalizeDailyVisits = (payload) => {
  if (!payload) return [];

  const pickArray = (candidate) => {
    if (!candidate) return null;
    if (Array.isArray(candidate)) return candidate;
    if (Array.isArray(candidate?.data)) return candidate.data;
    if (Array.isArray(candidate?.results)) return candidate.results;
    if (Array.isArray(candidate?.items)) return candidate.items;
    if (Array.isArray(candidate?.rows)) return candidate.rows;
    return null;
  };

  const list =
    pickArray(payload) ??
    pickArray(payload?.days) ??
    pickArray(payload?.daily_visits) ??
    pickArray(payload?.dailyVisits) ??
    pickArray(payload?.visits) ??
    pickArray(payload?.payload) ??
    pickArray(payload?.data) ??
    pickArray(payload?.result) ??
    pickArray(payload?.response) ??
    pickArray(payload?.meta) ??
    [];

  return list
    .map((item) => {
      if (!item) return null;
      const date =
        item.date ??
        item.Day ??
        item.day ??
        item.visit_date ??
        item.visitDate ??
        item.visited_on ??
        item.dateLabel;
      if (!date) return null;
      const rawCount =
        item.total_visits ??
        item.visit_count ??
        item.visits ??
        item.count ??
        item.total ??
        item.totalVisits ??
        item.total_visit ??
        item.total_visit_count;
      const normalizedCount =
        typeof rawCount === "number" ? rawCount : Number(rawCount ?? 0);
      return {
        ...item,
        date,
        total_visits: Number.isFinite(normalizedCount) ? normalizedCount : 0,
      };
    })
    .filter(Boolean);
};

const parseDailyVisitsPayload = (payload) => {
  return {
    points: normalizeDailyVisits(payload),
    availableOptions: normalizeAvailableMonths(payload?.available_months),
  };
};

const parseDailyAmountsPayload = (payload) => {
  const points = normalizeDailyAmounts(payload);
  let monthKey = null;
  if (payload?.year != null && payload?.month != null) {
    monthKey = `${payload.year}-${String(payload.month).padStart(2, "0")}`;
  } else if (points.length) {
    monthKey = getMonthKey(points[0]?.date);
  }
  const availableOptions = normalizeAvailableMonths(payload?.available_months);
  return {
    points,
    monthKey,
    availableOptions,
  };
};

function fetchReportWithFallback({ key, urls, headers, body, label }) {
  const tryFetch = (index = 0) =>
    cachedPost({ key, url: urls[index], headers, body }).catch((err) => {
      if (index >= urls.length - 1) throw err;
      if (label) {
        console.warn(`${label} failed for ${urls[index]}, trying fallback`, err);
      } else {
        console.warn(`Request failed for ${urls[index]}, trying fallback`, err);
      }
      return tryFetch(index + 1);
    });
  return tryFetch();
}

const groupPointsByMonth = (points) => {
  const buckets = new Map();
  if (!Array.isArray(points)) return buckets;
  points.forEach((point) => {
    const key = getMonthKey(point?.date);
    if (!key) return;
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(point);
  });
  return buckets;
};
const dropdownStyle = {
  borderRadius: "999px",
  border: "1px solid var(--surface-border)",
  padding: "0.35rem 0.85rem",
  background: "var(--surface-muted)",
  color: "var(--dark-blue)",
  fontSize: "0.9rem",
};

function Dashboard() {
  const { user } = useContext(UserContext);

  const [state, setState] = useState({
    avgDailyVisits: 0,
    avgTransactionAmount: 0,
    avgTransactionFee: 0,
      feeBalance: 0,
    topLocationsByVisits: [],
    dailyVisits: [],
    dailyAmounts: [],
    monthlySummary: [],
    topLocationsByAmount: [],
    visitMonthOptions: [ALL_MONTHS_OPTION],
    amountMonthOptions: [ALL_MONTHS_OPTION],
    error: false,
    loading: true,
  });
  const dailyVisitsCache = useRef(new Map());
  const dailyAmountsCache = useRef(new Map());
  const fetchVisitPromises = useRef(new Map());
  const fetchMonthPromises = useRef(new Map());
  useEffect(() => {
    dailyVisitsCache.current.clear();
    dailyAmountsCache.current.clear();
    fetchVisitPromises.current.clear();
    fetchMonthPromises.current.clear();
  }, [user.id]);

  const currencyFormatter = useMemo(
    () => new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }),
    []
  );
  const updatedLabel = useMemo(
    () =>
      new Intl.DateTimeFormat(undefined, {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      }).format(new Date()),
    []
  );
  const cacheKey = useMemo(
    () => (name) => `dash:v1:${api.main}:${user?.id ?? "anon"}:${name}`,
    [user?.id, api.main]
  );

  useEffect(() => {
    if (!user?.id || !user?.access_token) {
      setState(s => ({ ...s, error: true, loading: false }));
      return;
    }


    const headers = {
      Authorization: `Bearer ${user.access_token}`,
      "Content-Type": "application/json",
    };
    const body = JSON.stringify({ id: user.id, access_token: user.access_token });
    const k = cacheKey;

    // 1) Instant paint from cache if present
    const dailyAmountsKey = k("daily_transaction_amounts");
    const legacyDailyAmountsKey = k("daily_transaction_amount");

    const avgVisitsKey = k("avg_daily_visits");
    const cachedDailyAmountsPayload =
      readFreshCache(dailyAmountsKey) ?? readFreshCache(legacyDailyAmountsKey);
    const parsedCachedAmounts = parseDailyAmountsPayload(cachedDailyAmountsPayload);
    const sortedCachedAmounts = sortChronologically(parsedCachedAmounts.points, "date");
    const cachedBuckets = groupPointsByMonth(sortedCachedAmounts);
    cachedBuckets.forEach((list, key) => {
      dailyAmountsCache.current.set(key, list);
    });
    const cachedAmountOptions = mergeMonthOptions(
      [ALL_MONTHS_OPTION],
      parsedCachedAmounts.availableOptions,
      sortedCachedAmounts
    );

    const cachedDailyVisitsPayload = readFreshCache(k("daily_visits"));
    const parsedCachedVisits = parseDailyVisitsPayload(cachedDailyVisitsPayload);
    const sortedCachedVisits = sortChronologically(parsedCachedVisits.points, "date");
    const cachedVisitBuckets = groupPointsByMonth(sortedCachedVisits);
    cachedVisitBuckets.forEach((list, key) => {
      dailyVisitsCache.current.set(key, list);
    });
    const cachedVisitOptions = mergeMonthOptions(
      [ALL_MONTHS_OPTION],
      parsedCachedVisits.availableOptions,
      sortedCachedVisits
    );
    const cachedMonthlySummary = readFreshCache(k("monthly_summary"));

    const cachedState = {
      avgDailyVisits: readFreshCache(avgVisitsKey)?.avg_daily_visits,
      avgTransactionAmount: readFreshCache(k("avg_transaction_amount"))?.avg_transaction_amount,
      avgTransactionFee: readFreshCache(k("avg_transaction_fee"))?.avg_transaction_fee,
        feeBalance: readFreshCache(k("fee_balance"))?.fee_balance,
      topLocationsByVisits: readFreshCache(k("top_locations_by_visits")),
      dailyVisits: sortedCachedVisits,
      dailyAmounts: sortedCachedAmounts,
      monthlySummary: sortChronologically(cachedMonthlySummary, "month"),
      topLocationsByAmount: readFreshCache(k("top_locations_by_amount")),
      visitMonthOptions: cachedVisitOptions.length ? cachedVisitOptions : [ALL_MONTHS_OPTION],
      amountMonthOptions: cachedAmountOptions.length ? cachedAmountOptions : [ALL_MONTHS_OPTION],
    };

    const haveAny = Object.entries(cachedState).some(([key, value]) => {
      if (key === "visitMonthOptions" || key === "amountMonthOptions") return false;
      return value && (Array.isArray(value) ? value.length > 0 : true);
    });

    if (haveAny) {
      setState(s => ({
        ...s,
        avgDailyVisits: cachedState.avgDailyVisits ?? 0,
        avgTransactionAmount: cachedState.avgTransactionAmount ?? 0,
        avgTransactionFee: cachedState.avgTransactionFee ?? 0,
          feeBalance: cachedState.feeBalance ?? 0,
        topLocationsByVisits: cachedState.topLocationsByVisits ?? [],
        dailyVisits: cachedState.dailyVisits ?? [],
        dailyAmounts: cachedState.dailyAmounts ?? [],
        monthlySummary: cachedState.monthlySummary ?? [],
        topLocationsByAmount: cachedState.topLocationsByAmount ?? [],
        visitMonthOptions: cachedState.visitMonthOptions ?? s.visitMonthOptions,
        amountMonthOptions: cachedState.amountMonthOptions ?? s.amountMonthOptions,
        error: false,
        loading: false,
      }));
    }

    // 2) Fetch only if cache missing/expired (24h TTL handled inside cachedPost)
    let cancelled = false;

    const fetchWithFallback = (key, urls) => {
      const tryFetch = (index = 0) =>
        cachedPost({ key, url: urls[index], headers, body }).catch((err) => {
          if (index >= urls.length - 1) throw err;
          console.warn(`Primary endpoint failed for ${urls[index]}, trying fallback`, err);
          return tryFetch(index + 1);
        });
      return tryFetch();
    };

    Promise.all([
      cachedPost({
        key: avgVisitsKey,
        url: `${api.main}api/report/avg_daily_visits`,
        headers,
        body,
      }),
      fetchReportWithFallback({
        key: k("avg_transaction_amount"),
        urls: [
          `${api.main}api/report/avg_transaction_amount`,
          `${api.main}api/report/avg_daily_transaction_amount`,
          `${api.main}api/avg_transaction_amount`,
        ],
        headers,
        body,
        label: "Avg transaction amount",
      }),
      fetchReportWithFallback({
        key: k("avg_transaction_fee"),
        urls: [
          `${api.main}api/report/avg_transaction_fee`,
          `${api.main}api/avg_transaction_fee`,
        ],
        headers,
        body,
        label: "Avg transaction fee",
      }),
      fetchReportWithFallback({
        key: k("fee_balance"),
        urls: [
          `${api.main}api/report/fee_balance`,
          `${api.main}api/fee_balance`,
        ],
        headers,
        body,
        label: "Fee Balance",
      }),
      fetchReportWithFallback({
        key: k("top_locations_by_visits"),
        urls: [
          `${api.main}api/report/top_locations_by_visits`,
          `${api.main}api/top_locations_by_visits`,
        ],
        headers,
        body,
        label: "Top locations by visits",
      }),
      fetchReportWithFallback({
        key: k("daily_visits"),
        urls: [
          `${api.main}api/report/daily_visits`,
          `${api.main}api/daily_visits`,
          `${api.main}api/report/daily_visit`,
          `${api.main}api/daily_visit`,
        ],
        headers,
        body,
        label: "Daily visits",
      }),
      fetchReportWithFallback({
        key: dailyAmountsKey,
        urls: [
          `${api.main}api/report/daily_transaction_amount`,
          `${api.main}api/daily_transaction_amount`,
          `${api.main}api/report/daily_transaction_amounts`,
          `${api.main}api/daily_transaction_amounts`,
        ],
        headers,
        body,
        label: "Daily transaction amount",
      }),
      fetchReportWithFallback({
        key: k("monthly_summary"),
        urls: [
          `${api.main}api/report/monthly_summary`,
          `${api.main}api/monthly_summary`,
        ],
        headers,
        body,
        label: "Monthly summary",
      }),
      fetchReportWithFallback({
        key: k("top_locations_by_amount"),
        urls: [
          `${api.main}api/report/top_locations_by_amount`,
          `${api.main}api/top_locations_by_amount`,
        ],
        headers,
        body,
        label: "Top locations by amount",
      }),
    ])
      .then(([avgVisits, avgAmount, avgFee, feeBalance, topVisits, visits, amounts, monthly, topAmount]) => {
        if (cancelled) return;
        const parsedAmounts = parseDailyAmountsPayload(amounts);
        const parsedVisits = parseDailyVisitsPayload(visits);
        const sortedVisits = sortChronologically(parsedVisits.points, "date");
        const sortedAmounts = sortChronologically(parsedAmounts.points, "date");
        const sortedMonthly = sortChronologically(monthly, "month");
        const visitBuckets = groupPointsByMonth(sortedVisits);
        visitBuckets.forEach((list, key) => {
          dailyVisitsCache.current.set(key, list);
        });
        const amountBuckets = groupPointsByMonth(sortedAmounts);
        amountBuckets.forEach((list, key) => {
          dailyAmountsCache.current.set(key, list);
        });
        const visitOptions = combineMonthOptions(
          normalizeAvailableMonths(visits?.available_months),
          buildMonthEntries(sortedVisits)
        );
        const amountOptions = combineMonthOptions(
          parsedAmounts.availableOptions,
          buildMonthEntries(sortedAmounts)
        );
        setState(s => {
          const mergedVisitOptions = mergeMonthOptions(
            s.visitMonthOptions,
            parsedVisits.availableOptions,
            sortedVisits
          );
          const mergedAmountOptions = mergeMonthOptions(
            s.amountMonthOptions,
            parsedAmounts.availableOptions,
            sortedAmounts
          );
          return {
            ...s,
            avgDailyVisits: avgVisits?.avg_daily_visits ?? 0,
            avgTransactionAmount: avgAmount?.avg_transaction_amount ?? 0,
            avgTransactionFee: avgFee?.avg_transaction_fee ?? 0,
              feeBalance: feeBalance?.fee_balance ?? 0,
            topLocationsByVisits: topVisits ?? [],
            dailyVisits: sortedVisits,
            dailyAmounts: sortedAmounts,
            monthlySummary: sortedMonthly,
            topLocationsByAmount: topAmount ?? [],
            visitMonthOptions: mergedVisitOptions,
            amountMonthOptions: mergedAmountOptions,
            error: false,
            loading: false,
          };
        });
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Dashboard fetch error", err);
        setState(s => ({ ...s, error: true, loading: false }));
      });

    return () => { cancelled = true; };
  }, [user.id, user.access_token, cacheKey]);

  const {
    avgDailyVisits,
    avgTransactionAmount,
    avgTransactionFee,
    feeBalance,
    topLocationsByVisits,
    dailyVisits,
    dailyAmounts,
    monthlySummary,
    topLocationsByAmount,
    visitMonthOptions,
    amountMonthOptions,
    error,
    loading,
  } = state;

  const currentMonthKey = useMemo(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    return `${year}-${month}`;
  }, []);

  const [visitMonth, setVisitMonth] = useState(currentMonthKey);
  const [amountMonth, setAmountMonth] = useState(currentMonthKey);
  const visitSelectionLocked = useRef(false);
  const amountSelectionLocked = useRef(false);

  const tooltipBoxStyle = useMemo(
    () => ({
      backgroundColor: "#ffffff",
      border: "1px solid #ccc",
      padding: "10px 12px",
      borderRadius: "10px",
      boxShadow: "0 4px 12px rgba(15, 23, 42, 0.12)",
      maxWidth: 220,
    }),
    []
  );

  const renderVisitsTooltip = useCallback(
    ({ active, payload, label }) => {
      if (!active || !payload?.length) return null;
      const value = payload[0]?.value ?? 0;
      return (
        <div style={tooltipBoxStyle}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
          <div style={{ color: "var(--text-primary)" }}>Total Visits: {value}</div>
        </div>
      );
    },
    [tooltipBoxStyle]
  );

  const renderAmountTooltip = useCallback(
    ({ active, payload, label }) => {
      if (!active || !payload?.length) return null;
      const value = payload[0]?.value ?? 0;
      return (
        <div style={tooltipBoxStyle}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
          <div style={{ color: "var(--text-primary)" }}>Total Volume: {currencyFormatter.format(value)}</div>
        </div>
      );
    },
    [currencyFormatter, tooltipBoxStyle]
  );

  const renderMonthlySummaryTooltip = useCallback(
    ({ active, payload, label }) => {
      if (!active || !payload?.length) return null;
      const visits = payload.find((item) => item.dataKey === "total_visits")?.value ?? 0;
      const amount = payload.find((item) => item.dataKey === "total_amount")?.value ?? 0;
      return (
        <div style={tooltipBoxStyle}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
          <div>Visits: {visits}</div>
          <div>Processed Volume: {currencyFormatter.format(amount)}</div>
        </div>
      );
    },
    [currencyFormatter, tooltipBoxStyle]
  );

  const updateVisitOptions = useCallback((points, availableOptions) => {
    setState((s) => {
      const mergedOptions = mergeMonthOptions(s.visitMonthOptions, availableOptions, points);
      if (!mergedOptions.length) return s;
      return { ...s, visitMonthOptions: mergedOptions };
    });
  }, []);

  const aggregateAllVisits = useCallback(() => {
    const aggregated = [];
    dailyVisitsCache.current.forEach((points) => aggregated.push(...points));
    const sortedAggregated = sortChronologically(aggregated, "date");
    setState((s) => ({ ...s, dailyVisits: sortedAggregated }));
  }, []);

  const updateAmountOptions = useCallback((points, availableOptions) => {
    setState((s) => {
      const mergedOptions = mergeMonthOptions(s.amountMonthOptions, availableOptions, points);
      if (!mergedOptions.length) return s;
      return { ...s, amountMonthOptions: mergedOptions };
    });
  }, []);

  const fetchSeriesForMonth = useCallback(
    ({ monthKey, cacheRef, promiseRef, cacheNamespace, urls, parsePayload, updateOptions }) => {
      if (!monthKey || monthKey === ALL_MONTHS_OPTION.value) return Promise.resolve([]);
      if (cacheRef.current.has(monthKey)) {
        return Promise.resolve(cacheRef.current.get(monthKey));
      }
      if (promiseRef.current.has(monthKey)) {
        return promiseRef.current.get(monthKey);
      }
      if (!user?.id || !user?.access_token) return Promise.resolve([]);

      const [yearStr, monthStr] = monthKey.split("-");
      const year = Number(yearStr);
      const month = Number(monthStr);
      if (!year || !month) return Promise.resolve([]);

      const headers = {
        Authorization: `Bearer ${user.access_token}`,
        "Content-Type": "application/json",
      };
      const body = JSON.stringify({
        id: user.id,
        access_token: user.access_token,
        year,
        month,
      });

      const promise = fetchReportWithFallback({
        key: cacheKey(`${cacheNamespace}:${monthKey}`),
        urls,
        headers,
        body,
        label: `${cacheNamespace} fetch`,
      })
        .then((response) => {
          const parsed = parsePayload(response);
          const sorted = sortChronologically(parsed.points, "date");
          const buckets = groupPointsByMonth(sorted);
          buckets.forEach((list, key) => {
            cacheRef.current.set(key, list);
          });
          updateOptions(sorted, parsed.availableOptions);
          return cacheRef.current.get(monthKey) ?? [];
        })
        .finally(() => {
          promiseRef.current.delete(monthKey);
        });

      promiseRef.current.set(monthKey, promise);
      return promise;
    },
    [cacheKey, user?.access_token, user?.id]
  );

  const fetchVisitData = useCallback(
    (monthKey) =>
      fetchSeriesForMonth({
        monthKey,
        cacheRef: dailyVisitsCache,
        promiseRef: fetchVisitPromises,
        cacheNamespace: "daily_visits",
        urls: [
          `${api.main}api/report/daily_visits`,
          `${api.main}api/daily_visits`,
          `${api.main}api/report/daily_visit`,
          `${api.main}api/daily_visit`,
        ],
        parsePayload: parseDailyVisitsPayload,
        updateOptions: updateVisitOptions,
      }),
    [api.main, fetchSeriesForMonth, updateVisitOptions]
  );

  const aggregateAllMonths = useCallback(() => {
    const aggregated = [];
    dailyAmountsCache.current.forEach((points) => aggregated.push(...points));
    const sortedAggregated = sortChronologically(aggregated, "date");
    setState((s) => ({ ...s, dailyAmounts: sortedAggregated }));
  }, []);

  const fetchMonthData = useCallback(
    (monthKey) =>
      fetchSeriesForMonth({
        monthKey,
        cacheRef: dailyAmountsCache,
        promiseRef: fetchMonthPromises,
        cacheNamespace: "daily_transaction_amount",
        urls: [
          `${api.main}api/report/daily_transaction_amount`,
          `${api.main}api/daily_transaction_amount`,
          `${api.main}api/report/daily_transaction_amounts`,
          `${api.main}api/daily_transaction_amounts`,
        ],
        parsePayload: parseDailyAmountsPayload,
        updateOptions: updateAmountOptions,
      }),
    [api.main, fetchSeriesForMonth, updateAmountOptions]
  );

  const syncSelectionWithOptions = (
    options,
    currentValue,
    setValue,
    lockRef
  ) => {
    const values = options.map((option) => option.value);
    const hasCurrentValue = values.includes(currentValue);
    if (!hasCurrentValue) {
      lockRef.current = false;
    }
    if (lockRef.current) return;

    if (values.includes(currentMonthKey)) {
      setValue(currentMonthKey);
      return;
    }
    if (options.length > 1) {
      setValue(options[1].value);
      return;
    }
    setValue(ALL_MONTHS_OPTION.value);
  };

  useEffect(() => {
    syncSelectionWithOptions(visitMonthOptions, visitMonth, setVisitMonth, visitSelectionLocked);
  }, [visitMonthOptions, visitMonth, currentMonthKey]);

  useEffect(() => {
    syncSelectionWithOptions(amountMonthOptions, amountMonth, setAmountMonth, amountSelectionLocked);
  }, [amountMonthOptions, amountMonth, currentMonthKey]);

  useEffect(() => {
    if (loading) return;
    if (!user?.id || !user?.access_token) return;
    if (!visitMonth) return;

    let cancelled = false;

    if (visitMonth === ALL_MONTHS_OPTION.value) {
      const optionKeys = visitMonthOptions.slice(1).map((option) => option.value).filter(Boolean);
      const missingKeys = optionKeys.filter((key) => !dailyVisitsCache.current.has(key));
      if (missingKeys.length === 0) {
        aggregateAllVisits();
        return;
      }
      Promise.all(missingKeys.map((key) => fetchVisitData(key)))
        .then(() => {
          if (cancelled) return;
          aggregateAllVisits();
        })
        .catch((err) => {
          console.error("Daily visit (all months) fetch error", err);
        });
      return () => { cancelled = true; };
    }

    fetchVisitData(visitMonth)
      .then((data) => {
        if (cancelled) return;
        const chartData = dailyVisitsCache.current.get(visitMonth) ?? data ?? [];
        setState((s) => ({ ...s, dailyVisits: chartData }));
      })
      .catch((err) => {
        console.error("Daily visit fetch error", err);
      });

    return () => { cancelled = true; };
  }, [
    aggregateAllVisits,
    fetchVisitData,
    visitMonth,
    visitMonthOptions,
    loading,
    user?.access_token,
    user?.id,
  ]);

  useEffect(() => {
    if (loading) return;
    if (!user?.id || !user?.access_token) return;
    if (!amountMonth) return;

    let cancelled = false;

    if (amountMonth === ALL_MONTHS_OPTION.value) {
      const optionKeys = amountMonthOptions.slice(1).map((option) => option.value).filter(Boolean);
      const missingKeys = optionKeys.filter((key) => !dailyAmountsCache.current.has(key));
      if (missingKeys.length === 0) {
        aggregateAllMonths();
        return;
      }
      Promise.all(missingKeys.map((key) => fetchMonthData(key)))
        .then(() => {
          if (cancelled) return;
          aggregateAllMonths();
        })
        .catch((err) => {
          console.error("Daily transaction amount (all months) fetch error", err);
        });
      return () => { cancelled = true; };
    }

    fetchMonthData(amountMonth)
      .then((data) => {
        if (cancelled) return;
        const chartData = dailyAmountsCache.current.get(amountMonth) ?? data ?? [];
        setState((s) => ({ ...s, dailyAmounts: chartData }));
      })
      .catch((err) => {
        console.error("Daily transaction amount fetch error", err);
      });

    return () => { cancelled = true; };
  }, [
    aggregateAllMonths,
    amountMonth,
    amountMonthOptions,
    fetchMonthData,
    loading,
    user?.access_token,
    user?.id,
  ]);

  const filteredDailyVisits = useMemo(() => {
    if (!Array.isArray(dailyVisits)) return [];
    if (visitMonth === ALL_MONTHS_OPTION.value) return dailyVisits;
    return dailyVisits.filter((item) => getMonthKey(item?.date) === visitMonth);
  }, [dailyVisits, visitMonth]);

  const filteredDailyAmounts = useMemo(() => {
    if (!Array.isArray(dailyAmounts)) return [];
    if (amountMonth === ALL_MONTHS_OPTION.value) return dailyAmounts;
    return dailyAmounts.filter((item) => getMonthKey(item?.date) === amountMonth);
  }, [dailyAmounts, amountMonth]);

  if (loading) {
    return (
      <div className="dashboard">
        <div className="page-header">
          <div>
            <p className="page-eyebrow">Overview</p>
            <h2>Dashboard</h2>
          </div>
        </div>
        <div className="loading dashboard-loading">
          <LoadingComponent />
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="page-header">
        <div>
          <p className="page-eyebrow">Overview</p>
          <h2>Dashboard</h2>
          <p className="section-subtitle">
            Monitor visits, revenue, and top performers across your ISO network.
          </p>
        </div>
        <div className="page-pill">Updated {updatedLabel}</div>
      </div>

      {error && (
        <div className="alert alert-error">
          Error loading dashboard data. Please ensure you're logged in and try again.
        </div>
      )}

      <section className="page-section">
        <div className="section-heading">
          <div>
            <p className="section-eyebrow">Key Metrics</p>
            <h3>Network Snapshot</h3>
          </div>
          <span className="section-subtitle">Average activity across your locations</span>
        </div>
        <div className="dashboard-averages">
          <div className="average-item">
            <p className="average-label">Average Daily Visits</p>
            <div className="separator"></div>
            <p className="average-value">{avgDailyVisits}</p>
          </div>
          <div className="average-item">
            <p className="average-label">Avg Transaction Amount</p>
            <div className="separator"></div>
            <p className="average-value">{currencyFormatter.format(avgTransactionAmount)}</p>
          </div>
          <div className="average-item">
            <p className="average-label">Avg Transaction Fee</p>
            <div className="separator"></div>
            <p className="average-value">{currencyFormatter.format(avgTransactionFee)}</p>
          </div>
          <div className="average-item">
            <p className="average-label">Current Fee Balance</p>
            <div className="separator"></div>
            <p className="average-value">{currencyFormatter.format(feeBalance)}</p>
          </div>
        </div>
      </section>

      <section className="page-section">
        <div className="section-heading">
          <div>
            <p className="section-eyebrow">Trendline</p>
            <h3>Monthly Summary (6 Months)</h3>
          </div>
          <span className="section-subtitle">Visits vs Processed Volume</span>
        </div>
        {monthlySummary.length ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={monthlySummary}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip content={renderMonthlySummaryTooltip} />
              <Legend />
              <Line type="monotone" dataKey="total_visits" stroke="#4C6EF8" dot={{ r: 4 }} name="Visits" />
              <Line type="monotone" dataKey="total_amount" stroke="#FF5B3C" dot={{ r: 4 }} name="Amount" />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-gray-600">No monthly data available.</div>
        )}
      </section>

      <div className="chart-grid">
        <section className="chart-card">
          <div className="section-heading">
            <div>
              <p className="section-eyebrow">Engagement</p>
              <h3>Daily Visits</h3>
            </div>
            <select
              value={visitMonth}
              onChange={(event) => {
                visitSelectionLocked.current = true;
                setVisitMonth(event.target.value);
              }}
              style={dropdownStyle}
              aria-label="Filter daily visits by month"
            >
              {visitMonthOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          {filteredDailyVisits.length ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={filteredDailyVisits}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  angle={-45}
                  textAnchor="end"
                  height={70}
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    if (Number.isNaN(date.getTime())) return value;
                    const month = String(date.getMonth() + 1).padStart(2, "0");
                    const day = String(date.getDate()).padStart(2, "0");
                    return `${month}-${day}`;
                  }}
                />
                <YAxis/>
                <Tooltip cursor={{ fill: "transparent" }} content={renderVisitsTooltip} />
                <Bar dataKey="total_visits" fill="#4C6EF8" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-gray-600">No visit data available.</div>
          )}
        </section>

        <section className="chart-card">
          <div className="section-heading">
            <div>
              <p className="section-eyebrow">Revenue</p>
              <h3>Daily Processed Volume</h3>
            </div>
            <select
              value={amountMonth}
              onChange={(event) => {
                amountSelectionLocked.current = true;
                setAmountMonth(event.target.value);
              }}
              style={dropdownStyle}
              aria-label="Filter daily transaction amounts by month"
            >
              {amountMonthOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          {filteredDailyAmounts.length ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={filteredDailyAmounts}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  angle={-45}
                  textAnchor="end"
                  height={70}
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    if (Number.isNaN(date.getTime())) return value;
                    const month = String(date.getMonth() + 1).padStart(2, "0");
                    const day = String(date.getDate()).padStart(2, "0");
                    return `${month}-${day}`;
                  }}
                />
                <YAxis />
                <Tooltip cursor={{ fill: "transparent" }} content={renderAmountTooltip} />
                <Bar dataKey="total_amount" fill="#FF5B3C" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-gray-600">No transaction amount data available.</div>
          )}
        </section>
      </div>

      <div className="chart-grid">
        <section className="chart-card">
          <div className="section-heading">
            <div>
              <p className="section-eyebrow">Visits</p>
              <h3>Top Locations by Visit</h3>
            </div>
          </div>
          {topLocationsByVisits.length ? (
            <div className="dashboard-top-list">
              {topLocationsByVisits.map((loc, i) => (
                <div key={i} className="top-card">
                  <span className="card-label">{loc.business_name}</span>
                  <span className="card-dots" />
                  <span className="card-value">{loc.visit_count}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-gray-600">No top location data available.</div>
          )}
        </section>

        <section className="chart-card">
          <div className="section-heading">
            <div>
              <p className="section-eyebrow">Leaders</p>
              <h3>Top Locations by Processed Volume</h3>
            </div>
          </div>
          {topLocationsByAmount.length ? (
            <div className="dashboard-top-list">
              {topLocationsByAmount.map((loc, i) => (
                <div key={i} className="top-card">
                  <span className="card-label">{loc.business_name}</span>
                  <span className="card-dots" />
                  <span className="card-value">{currencyFormatter.format(loc.total_amount ?? 0)}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-gray-600">No top location data available.</div>
          )}
        </section>
      </div>
    </div>
  );
}

export default withRouter(Dashboard);
